/**
 * Context for managing Freecode run options and result as well as the submit function.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { useStateRef } from 'hooks/useStateRef';
import { categories, TOURNAMENT_RUN_DEFAULT } from 'constants/tests';
import { executorDecompress } from 'functions/executorDecompress';
import { editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';

export const FreecodeContextRun = React.createContext();

export const FreecodeContextRunProvider = ({ children }) => {
  const { editorType } = useFreecodeContextLayout();
  const { test, testImpl, setShowFlowOverlay } = useFreecodeContextData();

  const [runFunction, setRunFunction] = useState(undefined);
  const [runResponse, setRunResponse] = useState(undefined);
  const [canvasStep, setCanvasStep] = useState(0);
  const [scoreTabScore, setScoreTabScore] = useState('');
  const [codeTestSolutions, setCodeTestSolutions] = useState({});
  const [consoleLogSteps, setConsoleLogSteps] = useState([]);
  const [isCanvasAnimationCompleted, setIsCanvasAnimationCompleted] = useState(false);
  const [isLevelChanged, setIsLevelChanged] = useState(false);

  const [levelToRun, setLevelToRun, levelToRunRef] = useStateRef(0);
  const [isRunning, setIsRunning, isRunningRef] = useStateRef(false);
  const [runHidden, setRunHidden, runHiddenRef] = useStateRef(false);
  const [saveOnRun, setSaveOnRun, saveOnRunRef] = useStateRef(
    localStorage.getItem('fsEditor-saveOnRun') === 'false' ? false : true
  );
  const [codeSol, setCodeSol, codeSolRef] = useStateRef('');
  const [language, setLanguage, languageRef] = useStateRef('python');
  const [runAgainst, setRunAgainst, runAgainstRef] = useStateRef(
    localStorage.getItem('fsEditor-runAgainst') || TOURNAMENT_RUN_DEFAULT
  );

  useEffect(() => {
    if (editorType === editorTypes.freecodeInspect) setRunHidden(true);
  }, [editorType, setRunHidden]);

  useEffect(() => {
    if (!runResponse?.levelResults?.length) return setScoreTabScore('');
    if (editorType === editorTypes.exercise && levelToRun !== runResponse?.levelToRun) {
      return setScoreTabScore('');
    }
    const totalScore = runResponse.levelResults.reduce(
      (accumulator, levelResult) => {
        accumulator.score += levelResult.scores?.[0] ?? 0;
        accumulator.maxScore += levelResult.maxScore;
        return accumulator;
      },
      { score: 0, maxScore: 0 }
    );
    if (totalScore.maxScore) setScoreTabScore(`${totalScore.score}/${totalScore.maxScore}`);
  }, [editorType, levelToRun, runResponse, setScoreTabScore]);

  useEffect(() => {
    setConsoleLogSteps(runResponse?.decompressed?.[levelToRun]?.steps ?? []);
  }, [levelToRun, runResponse?.decompressed]);

  const selectLevelToRun = useCallback(
    (desiredLevel) => {
      if (levelToRunRef.current === desiredLevel) return;
      if (editorType === editorTypes.exercise && test?._id)
        localStorage.setItem(`fsEditor-exercise-${test._id}-levelToRun`, desiredLevel);
      setLevelToRun(desiredLevel);
    },
    [editorType, levelToRunRef, setLevelToRun, test?._id]
  );

  // Remove active run data and fix levelToRun if necessary
  const unsetRunResponse = useCallback(
    (isExercise) => {
      if (!testImpl?.configLevels) return;
      if (!isExercise && levelToRun >= testImpl.configLevels.numPublic)
        selectLevelToRun(testImpl.configLevels.numPublic - 1);
      setRunResponse(undefined);
    },
    [levelToRun, selectLevelToRun, testImpl?.configLevels]
  );

  const updateRunResponse = useCallback(
    (newRunResponse) => {
      try {
        const isExercise = test?.category === categories.EXERCISE.category;
        if (!newRunResponse) return unsetRunResponse(isExercise);
        const decompressed = executorDecompress(newRunResponse);
        if (!isExercise) {
          if (decompressed.length < levelToRunRef.current + 1) selectLevelToRun(decompressed.length - 1);
          setRunResponse({ ...newRunResponse, decompressed });
        } else {
          let exerciseData = executorDecompress(testImpl.generated?.peekResponse);
          if (editorType === editorTypes.creator && test.category === categories.EXERCISE.category) {
            exerciseData = exerciseData.slice(0, testImpl.configLevels.numPublic + testImpl.configLevels.numHidden);
          } else {
            exerciseData = exerciseData.slice(0, testImpl.configLevels.numPublic);
          }
          exerciseData[levelToRunRef.current] = decompressed[0];
          setRunResponse({ ...newRunResponse, decompressed: exerciseData });
          return decompressed[0];
        }
      } catch (error) {
        setRunResponse(undefined);
        toast.error('Could not decompress run response');
        console.error(error);
      }
    },
    [
      unsetRunResponse,
      test?.category,
      selectLevelToRun,
      testImpl?.generated?.peekResponse,
      testImpl?.configLevels?.numPublic,
      testImpl?.configLevels?.numHidden,
      levelToRunRef,
      editorType,
    ]
  );

  const toggleSaveOnRun = () => {
    let newValue = !saveOnRunRef.current;
    localStorage.setItem('fsEditor-saveOnRun', newValue);
    setSaveOnRun(newValue);
  };

  const updateAndRunFlow = useCallback(
    (desiredLevel) => {
      if (!testImpl?.parsedFlow?.[desiredLevel]) return;
      selectLevelToRun(desiredLevel);
      setShowFlowOverlay(true);
    },
    [setShowFlowOverlay, testImpl, selectLevelToRun]
  );

  return (
    <FreecodeContextRun.Provider
      value={{
        runFunction,
        setRunFunction,
        runResponse,
        updateRunResponse,
        canvasStep,
        setCanvasStep,
        levelToRun,
        setLevelToRun,
        levelToRunRef,
        selectLevelToRun,
        scoreTabScore,
        codeTestSolutions,
        setCodeTestSolutions,
        isRunning,
        setIsRunning,
        isRunningRef,
        codeSol,
        setCodeSol,
        codeSolRef,
        language,
        setLanguage,
        languageRef,
        runHidden,
        setRunHidden,
        runHiddenRef,
        saveOnRun,
        toggleSaveOnRun,
        saveOnRunRef,
        runAgainst,
        setRunAgainst,
        runAgainstRef,
        consoleLogSteps,
        setConsoleLogSteps,
        updateAndRunFlow,
        isCanvasAnimationCompleted,
        setIsCanvasAnimationCompleted,
        isLevelChanged,
        setIsLevelChanged,
      }}
    >
      {children}
    </FreecodeContextRun.Provider>
  );
};
